import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getOutfitById, deleteOutfit, toggleOutfitVisibility, Outfit, OutfitItem } from '@/services/outfitsService';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { ArrowLeft, Trash2, Eye, EyeOff, ImageIcon, ExternalLink, Loader2 } from 'lucide-react';

const OutfitsDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [outfit, setOutfit] = useState<Outfit | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getOutfitById(id);
        setOutfit(data);
      } catch (error: any) {
        console.error('Error fetching outfit:', error);
        toast.error('Failed to load outfit details');
        navigate('/admin/outfits');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!outfit) return;
    try {
      setDeleting(true);
      await deleteOutfit(outfit.id);
      toast.success('Outfit deleted');
      navigate('/admin/outfits');
    } catch (error: any) {
      console.error('Error deleting outfit:', error);
      toast.error('Failed to delete outfit');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleVisibility = async () => {
    if (!outfit) return;
    try {
      setToggling(true);
      await toggleOutfitVisibility(outfit.id, !outfit.is_public);
      setOutfit(prev => prev ? { ...prev, is_public: !prev.is_public } : null);
      toast.success(`Outfit ${outfit.is_public ? 'hidden' : 'published'}`);
    } catch (error: any) {
      console.error('Error toggling visibility:', error);
      toast.error('Failed to update visibility');
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-2">
          <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Loading outfit...</span>
        </div>
      </div>
    );
  }

  if (!outfit) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Outfit not found</p>
        <Button asChild variant="outline" className="mt-4">
          <Link to="/admin/outfits">Back to Outfits</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin/outfits">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight">{outfit.title}</h1>
          <p className="text-muted-foreground">
            Created by {outfit.user_email || outfit.user_name || 'Unknown'} · {new Date(outfit.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleToggleVisibility}
            disabled={toggling}
          >
            {toggling ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : outfit.is_public ? (
              <EyeOff className="h-4 w-4 mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            {outfit.is_public ? 'Hide' : 'Publish'}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-background border border-border/50">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Outfit</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete "{outfit.title}" and all its items. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-background hover:bg-muted">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-white hover:bg-destructive/90"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main image */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Outfit Image</CardTitle>
          </CardHeader>
          <CardContent>
            {outfit.image_url ? (
              <div className="rounded-lg overflow-hidden bg-muted">
                <img
                  src={outfit.image_url}
                  alt={outfit.title}
                  className="w-full h-auto max-h-[500px] object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/placeholder.svg';
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <ImageIcon className="h-12 w-12" />
                  <p>No image</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Details sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <Badge variant={outfit.is_public ? 'default' : 'secondary'} className="mt-1">
                  {outfit.is_public ? 'Public' : 'Private'}
                </Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Views</p>
                <p className="text-2xl font-bold">{outfit.view_count}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Likes</p>
                <p className="text-2xl font-bold">{outfit.like_count}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Created</p>
                <p>{new Date(outfit.created_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">Last Updated</p>
                <p>{new Date(outfit.updated_at).toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>

          {outfit.tags && outfit.tags.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Tags</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {outfit.tags.map((tag, i) => (
                    <Badge key={i} variant="secondary">{tag}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Description */}
      {outfit.description && (
        <Card>
          <CardHeader>
            <CardTitle>Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground whitespace-pre-wrap">{outfit.description}</p>
          </CardContent>
        </Card>
      )}

      {/* Items */}
      <Card>
        <CardHeader>
          <CardTitle>Items ({outfit.items?.length || 0})</CardTitle>
          <CardDescription>Products linked to this outfit</CardDescription>
        </CardHeader>
        <CardContent>
          {(!outfit.items || outfit.items.length === 0) ? (
            <p className="text-muted-foreground text-center py-8">No items in this outfit</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {outfit.items.map((item: OutfitItem) => (
                <div key={item.id} className="border rounded-lg overflow-hidden bg-card">
                  <div className="h-40 bg-muted overflow-hidden">
                    {item.image_url ? (
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder.svg';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-8 w-8" />
                      </div>
                    )}
                  </div>
                  <div className="p-3 space-y-1">
                    <p className="text-sm font-medium truncate">{item.title}</p>
                    {item.brand && (
                      <p className="text-xs text-muted-foreground">{item.brand}</p>
                    )}
                    {item.price && (
                      <p className="text-sm font-semibold text-primary">${item.price.toFixed(2)}</p>
                    )}
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" />
                        View product
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OutfitsDetail;
